import React, { useEffect, useMemo } from 'react';
import { useLevelsStore } from '../../store/levelsStore';
import { useFlowSettingsStore } from '../../store/flowSettingsStore';
import {
  useFlowSignalsStore,
  flowReading,
  flowToggleKey,
  flowEdge,
  flowEdgeLine,
  EDGE_T_RELIABLE,
  fmtFlowNumber,
  fmtFlowTime,
  FLOW_MASTER_KEY,
  type FlowSignal,
} from '../../store/flowSignalsStore';
import { flowGlyph } from '../Chart/FlowSignalMarkerPrimitive';
import { normalizeToBase } from '../../utils/symbolUtils';
import { FLOW_SIGNAL_CONFIGS, FLOW_CONTEXT_CONFIGS, describeFlowSignal } from './flowConfigs';
import { FLOW_SETTINGS_ROOTS } from './flowSettings';
import type { TradeFinderTabProps } from './tabs';
import DisplaySection from './DisplaySection';

/** Rows in the recent-signals list. */
export const RECENT_SIGNALS_LIMIT = 50;

const EMPTY: FlowSignal[] = [];

/** One TradeFinder switch row (Levels-panel look); also used by the Lines tab. */
export const Switch: React.FC<{
  on: boolean;
  label: string;
  description?: string;
  glyph?: string;
  /** Colour dot in place of a glyph (line colour). */
  swatch?: string;
  /** Right-aligned extra (e.g. the line's latest value). */
  meta?: string;
  muted?: boolean;
  testId: string;
  onChange: (on: boolean) => void;
}> = ({ on, label, description, glyph, swatch, meta, muted, testId, onChange }) => (
  <div className={`levels-row ${on ? 'is-on' : ''} ${muted ? 'tradefinder-muted' : ''}`}>
    <div className="levels-row-main">
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={label}
        className={`levels-item ${on ? 'active' : ''}`}
        onClick={() => onChange(!on)}
        title={description}
        data-testid={testId}
      >
        <span className="levels-row-dots tradefinder-glyph" aria-hidden="true">
          {swatch ? <span className="tradefinder-swatch" style={{ background: swatch }} /> : glyph}
        </span>
        <span className="levels-row-text">
          <span className="levels-item-label">{label}</span>
          {description && <span className="levels-row-desc">{description}</span>}
        </span>
        {meta && <span className="tradefinder-line-value">{meta}</span>}
        <span className="levels-switch" aria-hidden="true"><span className="levels-switch-knob" /></span>
      </button>
    </div>
  </div>
);

/**
 * Newest first, only kinds whose switch is on (so the list matches what the
 * chart is asked to draw), capped.
 */
export function recentSignals(
  signals: readonly FlowSignal[],
  shown: Partial<Record<string, boolean>>,
  limit = RECENT_SIGNALS_LIMIT,
): FlowSignal[] {
  const out: FlowSignal[] = [];
  for (let i = signals.length - 1; i >= 0 && out.length < limit; i -= 1) {
    const key = flowToggleKey(signals[i].kind);
    if (key && shown[key]) out.push(signals[i]);
  }
  return out;
}

const SignalsTab: React.FC<TradeFinderTabProps> = ({ symbol, onJumpTo }) => {
  const visibility = useLevelsStore((s) => s.visibility);
  const setLevelVisible = useLevelsStore((s) => s.setLevelVisible);
  const offline = useFlowSignalsStore((f) => f.status?.connected === false);
  const signals = useFlowSignalsStore((f) => f.bySymbol[normalizeToBase(symbol)] ?? EMPTY);
  const master = !!visibility[FLOW_MASTER_KEY];
  const recent = useMemo(() => recentSignals(signals, visibility), [signals, visibility]);

  // Descriptions quote the root's effective thresholds once its settings are
  // loaded (shared with the Settings tab); numberless until then or when
  // flow-signals is unreachable.
  const base = normalizeToBase(symbol);
  const settingsRoot = (FLOW_SETTINGS_ROOTS as readonly string[]).includes(base) ? base : null;
  const layers = useFlowSettingsStore((st) => (settingsRoot ? st.byRoot[settingsRoot]?.data?.layers : undefined));
  const settingsRequested = useFlowSettingsStore((st) => (settingsRoot ? st.byRoot[settingsRoot] !== undefined : true));
  useEffect(() => {
    if (settingsRoot && !settingsRequested) void useFlowSettingsStore.getState().fetchSettings(settingsRoot);
  }, [settingsRoot, settingsRequested]);

  return (
    <div className="tradefinder-signals" data-testid="tradefinder-signals-tab">
      <Switch
        on={master}
        label="Show TradeFinder markers"
        description="Every order-flow marker on the chart; the switches below pick the kinds"
        testId={`tradefinder-toggle-${FLOW_MASTER_KEY}`}
        onChange={(on) => setLevelVisible(FLOW_MASTER_KEY, on)}
      />
      {offline && (
        <div className="levels-note tradefinder-offline" data-testid="tradefinder-offline">flow-signals offline</div>
      )}
      <div className="levels-group-heading">Kinds</div>
      {FLOW_SIGNAL_CONFIGS.map((c) => (
        <Switch
          key={c.key}
          on={!!visibility[c.key]}
          label={c.label}
          description={describeFlowSignal(c, layers)}
          glyph={flowGlyph({ kind: c.kind }).symbol}
          muted={!master}
          testId={`tradefinder-toggle-${c.key}`}
          onChange={(on) => setLevelVisible(c.key, on)}
        />
      ))}
      <div className="levels-group-heading" data-testid="tradefinder-context-heading">Context (research, not signals)</div>
      {FLOW_CONTEXT_CONFIGS.map((c) => (
        <Switch
          key={c.key}
          on={!!visibility[c.key]}
          label={c.label}
          description={describeFlowSignal(c, layers)}
          glyph={c.glyph}
          muted={!master}
          testId={`tradefinder-toggle-${c.key}`}
          onChange={(on) => setLevelVisible(c.key, on)}
        />
      ))}
      <DisplaySection symbol={symbol} />
      <div className="levels-group-heading">Recent on {normalizeToBase(symbol) || 'this chart'}</div>
      {recent.length === 0 ? (
        <div className="levels-empty" data-testid="tradefinder-signals-empty">No signals yet for the kinds that are on</div>
      ) : (
        <ul className="tradefinder-recent" data-testid="tradefinder-recent">
          {recent.map((s) => {
            const reading = flowReading(s);
            const edge = flowEdge(s.detail);
            const body = (
              <>
                <span className="tradefinder-glyph" aria-hidden="true">{flowGlyph(s).symbol}</span>
                <span className="tradefinder-recent-main">
                  <span className="tradefinder-recent-label">{s.label || s.kind}</span>
                  <span className="tradefinder-recent-meta">
                    {s.price.toFixed(2)} · {fmtFlowTime(s.timestamp)} · {fmtFlowNumber(s.value)}
                    {edge && (
                      <>
                        {' · '}
                        <span
                          className={`tradefinder-edge ${edge.reliable ? 'is-reliable' : 'is-weak'}`}
                          title={edge.reliable
                            ? `tuner edge for this kind; |band t| >= ${EDGE_T_RELIABLE}`
                            : `|t| below ${EDGE_T_RELIABLE}: no reliable edge for this kind`}
                          data-testid="tradefinder-signal-edge"
                        >
                          {flowEdgeLine(s.detail)}
                        </span>
                      </>
                    )}
                  </span>
                </span>
              </>
            );
            return (
              <li
                key={s.id}
                className={`tradefinder-recent-item is-${reading}`}
                data-testid="tradefinder-signal-item"
                data-signal-id={s.id}
                title={`${reading} reading`}
              >
                {onJumpTo ? (
                  <button type="button" className="tradefinder-recent-btn" onClick={() => onJumpTo(s.timestamp)}>
                    {body}
                  </button>
                ) : body}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default SignalsTab;
